import React, { memo, useMemo } from 'react';
import { AttemptState, CalculatedOrderStatus, Delay } from '../../models';
import { Dayjs } from 'dayjs';
import './OrderSummaryBox.css';
import { useLanguage } from '../../locale/useLanguage';
import {
  getBorderColorClassByState,
  getIconByState,
  getFormattedDate,
  getFormattedTime,
  getTextFromAttemptNumber,
} from './orderSummaryBoxHelper';

type OrderSummaryBoxProps = {
  attemptNumber?: AttemptState;
  orderState: CalculatedOrderStatus;
  dateTime?: Dayjs | null;
  singleText?: string;
  isInLate?: boolean;
  arrivingDate?: Dayjs;
  delay?: Delay;
};

export const OrderSummaryBox: React.FC<OrderSummaryBoxProps> = memo(
  ({
    orderState,
    singleText,
    dateTime,
    attemptNumber,
    isInLate,
    arrivingDate,
    delay,
  }) => {
    const [t] = useLanguage();
    const borderColor = getBorderColorClassByState(orderState);
    const HeaderIcon = getIconByState(orderState);
    const formattedDate = useMemo(
      () => getFormattedDate(dateTime, t),
      [dateTime, t]
    );
    const formattedTime = useMemo(() => getFormattedTime(dateTime), [dateTime]);
    const attemptText = getTextFromAttemptNumber(
      attemptNumber,
      orderState,
      delay,
      t
    );

    return (
      <div className={`order-summary-box--container ${borderColor}`}>
        <div className="order-summary-box--box">
          {singleText || formattedDate}
        </div>
        {!singleText && (
          <>
            <div className={`order-summary-box--central-line ${borderColor}`} />
            <div className="order-summary-box--box">
              {!arrivingDate ? (
                <>
                  <span className="order-summary-box--font-normal">
                    {t('summaryBox.at')}
                  </span>
                  &nbsp;
                  {formattedTime}
                  {orderState === CalculatedOrderStatus.InTransit && isInLate && (
                    <>
                      &nbsp;
                      <span className="order-summary-box--font-normal">
                        {t('summaryBox.around')}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <div>
                  <span className="order-summary-box--font-normal">
                    {t('summaryBox.from')}
                  </span>
                  &nbsp;
                  {formattedTime}
                  &nbsp;
                  <br />
                  <span className="order-summary-box--font-normal">
                    {t('summaryBox.to')}
                  </span>
                  &nbsp;
                  {arrivingDate!.format('HH:mm')}
                </div>
              )}
            </div>
          </>
        )}
        <div className="order-summary-box--header-footer-container order-summary-box--header-container">
          {HeaderIcon && <HeaderIcon width="0.75rem" height="0.75rem" />}
          {HeaderIcon && <>&nbsp;&nbsp;</>}
          {attemptText}
        </div>
        <div className="order-summary-box--header-footer-container order-summary-box--footer-container">
          {t('summaryBox.near').toUpperCase()}
        </div>
      </div>
    );
  }
);
